import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import {
  AlertTriangle,
  Ban,
  Building2,
  Globe,
  MapPin,
  Pencil,
  ThumbsDown,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
} from "@/features/power_projects/components/ui";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useContractorOptions } from "@/features/rfq/api/proceedings";
import { LeadAttachmentsPanel } from "@/features/leads/components/LeadAttachmentsPanel";
import { LeadContactsPanel } from "@/features/leads/components/LeadContactsPanel";
import { LeadFormModal } from "@/features/leads/components/LeadFormModal";
import { LeadRichText } from "@/features/leads/components/LeadRichText";
import { LeadTasksPanel } from "@/features/leads/components/LeadTasksPanel";
import { LeadTimeline } from "@/features/leads/components/LeadTimeline";
import {
  STATUS_COLORS,
  useBlockLead,
  useConvertLead,
  useDeclineLead,
  useDeleteLead,
  useLead,
  useLeadDictionaries,
  useSetLeadStatus,
  useUnblockLead,
  type LeadStatus,
} from "@/features/leads/api/leads";

/**
 * Karta leada — całość tego, co o firmie wiemy, na jednym ekranie.
 *
 * Ostrzeżenie „ostatni ruch: X" jest celowo miękkie: dwie osoby dzwoniące tego
 * samego dnia do tej samej firmy to wpadka, ale system, który nie pozwala przejąć
 * leada po urlopowiczu, to przeszkoda.
 */

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: lead, isLoading } = useLead(id);
  const { data: dictionaries } = useLeadDictionaries();
  const { data: contractors } = useContractorOptions();
  const setStatus = useSetLeadStatus();
  const blockLead = useBlockLead();
  const unblockLead = useUnblockLead();
  const declineLead = useDeclineLead();
  const convert = useConvertLead();
  const removeLead = useDeleteLead();

  const [editing, setEditing] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [converting, setConverting] = useState(false);
  const [contractorId, setContractorId] = useState("");
  const [error, setError] = useState("");

  usePageTitle(lead ? `Lead — ${lead.name}` : "Lead");

  if (isLoading) return <PageLoader />;
  if (!lead) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-6">
        <p className="section-label">Nie znaleziono leada.</p>
      </div>
    );
  }

  const changeStatus = (status: LeadStatus) => {
    setError("");
    setStatus.mutate(
      { id: lead.id, status },
      {
        onError: (err: unknown) => {
          const errors = (
            err as {
              response?: { data?: { errors?: Record<string, unknown> } };
            }
          ).response?.data?.errors;
          const detail = errors && Object.values(errors)[0];
          setError(
            Array.isArray(detail)
              ? String(detail[0])
              : String(detail ?? "Nie udało się zmienić statusu."),
          );
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <PageHeader
        backTo="/leads"
        backLabel="Leady"
        title={
          <span className="flex flex-wrap items-center gap-2">
            {lead.name}
            <Badge color={STATUS_COLORS[lead.status]}>
              {lead.status_label}
            </Badge>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            {lead.nip && <span>NIP {lead.nip}</span>}
            {lead.regon && <span>REGON {lead.regon}</span>}
            {lead.krs && <span>KRS {lead.krs}</span>}
            {(lead.street || lead.city) && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} />
                {[
                  lead.street,
                  [lead.postal_code, lead.city].filter(Boolean).join(" "),
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
            {lead.website && (
              <a
                href={
                  lead.website.startsWith("http")
                    ? lead.website
                    : `https://${lead.website}`
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
              >
                <Globe size={11} />
                {lead.website}
              </a>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="input-base sm:w-auto"
              value={lead.status}
              onChange={(e) => changeStatus(e.target.value as LeadStatus)}
              disabled={lead.status === "blocked"}
            >
              {/* „Nie kontaktować" i „Nie chcemy współpracować" mają własne
                  przyciski: obie decyzje muszą zrobić coś więcej niż przestawić
                  pole (wpis na listę globalną / zatrzymanie kampanii), więc
                  backend odrzuca je z tej listy. Zostają w niej tylko jako
                  pozycja pokazująca stan bieżący. */}
              {(dictionaries?.statuses ?? [])
                .filter(
                  (option) =>
                    option.value !== "blocked" && option.value !== "declined",
                )
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              {lead.status === "blocked" && (
                <option value="blocked">Nie kontaktować</option>
              )}
              {lead.status === "declined" && (
                <option value="declined">Nie chcemy współpracować</option>
              )}
            </select>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
            >
              <Pencil size={13} className="mr-1.5 inline" /> Edytuj
            </Button>
            {!lead.contractor && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConverting(true)}
              >
                <Building2 size={13} className="mr-1.5 inline" /> Powiąż
              </Button>
            )}
            {lead.status !== "blocked" && lead.status !== "declined" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDeclining(true)}
                title="Nasza decyzja — zatrzymuje kampanie, nie trafia na listę sprzeciwów"
              >
                <ThumbsDown size={13} className="mr-1.5 inline" /> Nie chcemy
                współpracować
              </Button>
            )}
            {lead.status !== "blocked" && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setBlocking(true)}
              >
                <Ban size={13} className="mr-1.5 inline" /> Nie kontaktować
              </Button>
            )}
            {user?.is_ceo && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!confirm("Usunąć leada razem z całą historią kontaktu?"))
                    return;
                  removeLead.mutate(lead.id, {
                    onSuccess: () => navigate("/leads"),
                  });
                }}
              >
                <Trash2 size={13} />
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="mb-3 text-sm text-[var(--color-primary)]">{error}</p>
      )}

      <div className="mb-5 space-y-2">
        {lead.suppression && (
          <Notice tone="danger">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Firma jest na liście „nie kontaktować”
                {lead.suppression.reason ? ` — ${lead.suppression.reason}` : ""}.
                Żadna kampania nie wyśle do niej maila.
              </span>
              {/* Sprzeciw bywa pomyłką albo się dezaktualizuje (firma sama prosi o
                  kontakt) — zdjęcie zakazu ma być tam, gdzie widać zakaz, a nie na
                  drugim ekranie. Kasuje wszystkie wpisy firmy naraz. */}
              <Button
                size="sm"
                variant="secondary"
                loading={unblockLead.isPending}
                onClick={() => {
                  if (
                    !confirm(
                      "Zdjąć zakaz kontaktu? Firma wróci do lejka, ale zatrzymane kampanie NIE wznowią się same.",
                    )
                  )
                    return;
                  setError("");
                  unblockLead.mutate(
                    { id: lead.id, reason: "Zdjęte ręcznie z karty firmy." },
                    {
                      onError: () => setError("Nie udało się zdjąć zakazu."),
                    },
                  );
                }}
              >
                Zdejmij zakaz
              </Button>
            </div>
          </Notice>
        )}
        {/* Werdykt po naszej stronie. Sam pasek statusu mówi „nie chcemy
            współpracować", ale nie mówi DLACZEGO — a bez powodu pierwsza osoba,
            która na to trafi, odwróci decyzję albo zadzwoni do firmy ponownie. */}
        {lead.status === "declined" && (
          <Notice tone="warning">
            Oznaczono: <b>nie chcemy współpracować</b>
            {lead.decline_reason ? ` — ${lead.decline_reason}` : ""}. Kampanie do
            tej firmy stoją i nie da się jej dodać do nowej. Decyzję cofa zmiana
            statusu obok; zatrzymane kampanie i tak nie ruszą same.
          </Notice>
        )}
        {lead.existing_contractor && (
          <Notice tone="warning">
            {lead.existing_contractor.linked
              ? "Firma weszła do systemu jako "
              : "Ta firma już z nami współpracuje jako "}
            <Link
              to={`/contractors/${lead.existing_contractor.id}`}
              className="underline"
            >
              {lead.existing_contractor.name}
            </Link>
            {lead.existing_contractor.linked
              ? ". Historia pozyskania zostaje tutaj."
              : ". Nie wysyłaj zimnego zapytania."}
          </Notice>
        )}
        {lead.last_activity_by_name &&
          user &&
          lead.last_activity_by_name !==
            `${user.first_name} ${user.last_name}`.trim() && (
            <Notice tone="muted">
              Ostatni ruch na tym leadzie:{" "}
              {lead.last_activity_at
                ? formatDistanceToNow(new Date(lead.last_activity_at), {
                    addSuffix: true,
                    locale: pl,
                  })
                : "—"}{" "}
              — {lead.last_activity_by_name}. Zanim zadzwonisz, sprawdź
              historię.
            </Notice>
          )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_21rem]">
        <LeadTimeline lead={lead} />
        <div className="space-y-4">
          <SectionCard title="Metryka">
            <dl className="space-y-1.5 text-xs">
              <Row label="Prowadzący" value={lead.owner_name ?? "—"} />
              <Row
                label="Źródło"
                value={[lead.source_label, lead.source_detail]
                  .filter(Boolean)
                  .join(" — ")}
              />
              <Row
                label="Województwa"
                value={
                  lead.voivodeship_labels?.length
                    ? lead.voivodeship_labels.join(", ")
                    : "nie podano"
                }
              />
              <Row label="Dodał" value={lead.created_by_name ?? "—"} />
              <Row
                label="Dodano"
                value={format(new Date(lead.created_at), "dd.MM.yyyy")}
              />
              {lead.converted_at && (
                <Row
                  label="Wszedł do systemu"
                  value={format(new Date(lead.converted_at), "dd.MM.yyyy")}
                />
              )}
            </dl>
            {lead.notes && (
              <div className="mt-3 border-t border-[var(--color-border)] pt-2">
                <LeadRichText
                  content={lead.notes}
                  className="text-xs text-[var(--color-text-secondary)]"
                />
              </div>
            )}
          </SectionCard>

          <LeadContactsPanel lead={lead} />
          <LeadTasksPanel lead={lead} />
          <LeadAttachmentsPanel lead={lead} />
        </div>
      </div>

      <LeadFormModal
        open={editing}
        onClose={() => setEditing(false)}
        dictionaries={dictionaries}
        lead={lead}
      />

      <Modal
        open={blocking}
        onClose={() => setBlocking(false)}
        title="Nie kontaktować"
      >
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          NIP firmy i wszystkie znane adresy jej osób kontaktowych trafią na
          globalną listę blokującą. Sprzeciw działa niezależnie od kampanii i
          zostaje nawet po skasowaniu leada z bazy.
        </p>
        <input
          className="input-base mb-3"
          placeholder="Powód (np. sami prosili o wypisanie)"
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setBlocking(false)}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            loading={blockLead.isPending}
            onClick={() =>
              blockLead.mutate(
                { id: lead.id, reason: blockReason },
                {
                  onSuccess: () => {
                    setBlocking(false);
                    setBlockReason("");
                  },
                },
              )
            }
          >
            Dodaj do listy
          </Button>
        </div>
      </Modal>

      <Modal
        open={declining}
        onClose={() => setDeclining(false)}
        title="Nie chcemy współpracować"
      >
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          To NASZA decyzja, nie sprzeciw firmy — dlatego nie trafia na globalną
          listę „nie kontaktować”. Firma wypada z lejka, a jej kampanie stają
          natychmiast; cofniesz to, zmieniając status na karcie.
        </p>
        <textarea
          className="input-base mb-3"
          rows={3}
          placeholder="Powód (np. stawki dwa razy wyższe od rynkowych, brak uprawnień SEP)"
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
        />
        <p className="mb-3 text-[11px] text-[var(--color-text-muted)]">
          Powód jest wymagany — przeczyta go za pół roku ktoś, kto będzie chciał
          tę decyzję odwrócić.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDeclining(false)}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            disabled={!declineReason.trim()}
            loading={declineLead.isPending}
            onClick={() => {
              setError("");
              declineLead.mutate(
                { id: lead.id, reason: declineReason.trim() },
                {
                  onSuccess: () => {
                    setDeclining(false);
                    setDeclineReason("");
                  },
                  onError: () => setError("Nie udało się zapisać decyzji."),
                },
              );
            }}
          >
            Zapisz decyzję
          </Button>
        </div>
      </Modal>

      <Modal
        open={converting}
        onClose={() => setConverting(false)}
        title="Powiąż z podwykonawcą"
      >
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          Wskaż kartę podwykonawcy założoną dla tej firmy. Lead zostanie w bazie
          razem z całą historią pozyskania.
        </p>
        <select
          className="input-base mb-3"
          value={contractorId}
          onChange={(e) => setContractorId(e.target.value)}
        >
          <option value="">— wybierz firmę —</option>
          {(contractors ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConverting(false)}>
            Anuluj
          </Button>
          <Button
            disabled={!contractorId}
            loading={convert.isPending}
            onClick={() =>
              convert.mutate(
                { id: lead.id, contractor: contractorId },
                {
                  onSuccess: () => setConverting(false),
                  onError: () =>
                    setError(
                      "Nie udało się powiązać — sprawdź, czy firma nie jest już przypisana.",
                    ),
                },
              )
            }
          >
            Powiąż
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[var(--color-text-muted)]">{label}</dt>
      <dd className="text-right text-[var(--color-text-secondary)]">
        {value || "—"}
      </dd>
    </div>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: "danger" | "warning" | "muted";
  children: React.ReactNode;
}) {
  const className =
    tone === "danger"
      ? "border-[var(--color-primary)] text-[var(--color-primary)]"
      : tone === "warning"
        ? "border-amber-400 text-amber-600 dark:text-amber-500"
        : "border-[var(--color-border)] text-[var(--color-text-secondary)]";
  return (
    <p
      className={`flex items-start gap-2 border-l-2 bg-[var(--color-bg-elevated)] px-3 py-2 text-sm ${className}`}
    >
      {tone !== "muted" && (
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      )}
      <span>{children}</span>
    </p>
  );
}
